import React, { useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocation } from 'react-router-dom';
import { Button, Menu, MenuItem, ListItemText, Typography, Chip } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { powertrainAtom } from 'atoms/powertrainAtom';
import { stageConfigAtom as rStageConfigAtom } from 'atoms/Rtool/stageConfigAtoms';
import { stageConfigAtom as lStageConfigAtom } from 'atoms/Ltool/stageConfigAtoms';
import {
    recentCalcsAtom,
    recordRecentCalcAtom,
    buildCalcLabel,
    type RecentCalc,
    type RecentCalcConfig,
    type RecentCalcTool,
} from 'atoms/recentCalcsAtom';
import { useReselectCalc } from 'utils/hooks/useReselectCalc';
import { POWERTRAIN_COLORS } from 'styles/theme';

// 現在 route から操作中ツール（R/L）を判定する。対応する stageConfigAtoms を読む根拠（4. 実装上の制約）。
const toolFromPath = (pathname: string): RecentCalcTool | null =>
    pathname.startsWith('/l-tools') ? 'l' : pathname.startsWith('/r-tools') ? 'r' : null;

// 選択中計算セットの表示と、直近履歴ドロップダウンからの再選択を担う。
export const CalcSetIndicator: React.FC = () => {
    const { pathname } = useLocation();

    const powertrain = useAtomValue(powertrainAtom);
    const rConfig = useAtomValue(rStageConfigAtom);
    const lConfig = useAtomValue(lStageConfigAtom);
    const recent = useAtomValue(recentCalcsAtom);
    const record = useSetAtom(recordRecentCalcAtom);
    const reselectCalc = useReselectCalc();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const tool = toolFromPath(pathname);
    const activeConfig: RecentCalcConfig | null =
        tool === 'l' ? lConfig : tool === 'r' ? rConfig : null;
    const activeLabel = activeConfig ? buildCalcLabel(activeConfig) : '';
    const activeCalcId = activeConfig?.calculation_id ?? '';

    // 計算セット選択画面（existing-project）で calculation_id まで一意に定まったら直近履歴へ記録する。
    useEffect(() => {
        if (!tool || !activeConfig) return;
        if (!pathname.endsWith('/existing-project')) return;
        if (!activeCalcId) return;
        record({ tool, powertrain, config: activeConfig, label: activeLabel });
    }, [pathname, tool, activeConfig, activeCalcId, activeLabel, powertrain, record]);

    // 再選択は共通 hook（コマンドパレットと同一実装）へ委譲し、ここではメニューを閉じる責務のみ持つ。
    const reselect = (entry: RecentCalc) => {
        setAnchorEl(null);
        reselectCalc(entry);
    };

    return (
        <>
            <Button
                color="inherit"
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                startIcon={<HistoryIcon />}
                endIcon={<ArrowDropDownIcon />}
                aria-label="計算セット履歴"
                title="選択中の計算セット / 直近の計算セット"
                sx={{ textTransform: 'none', maxWidth: { xs: 160, sm: 320, md: 480 }, minWidth: 0 }}
            >
                <Typography variant="body2" component="span" noWrap>
                    {activeLabel ? `${tool?.toUpperCase()}: ${activeLabel}` : '計算セット未選択'}
                </Typography>
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>
                    直近の計算セット
                </Typography>
                {recent.length === 0 ? (
                    <MenuItem disabled>履歴はありません</MenuItem>
                ) : (
                    recent.map((entry) => (
                        <MenuItem
                            key={`${entry.tool}-${entry.powertrain}-${entry.label}`}
                            onClick={() => reselect(entry)}
                        >
                            <Chip
                                size="small"
                                label={`${entry.tool.toUpperCase()} · ${entry.powertrain.toUpperCase()}`}
                                sx={{
                                    mr: 1,
                                    bgcolor: POWERTRAIN_COLORS[entry.powertrain].main,
                                    color: POWERTRAIN_COLORS[entry.powertrain].contrast,
                                }}
                            />
                            <ListItemText primary={entry.label} />
                        </MenuItem>
                    ))
                )}
            </Menu>
        </>
    );
};
